import { SeoProductCombination } from 'src/pages/Product/Seo'

/* Next */
import Link from 'src/components/Link'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent, TouchEvent } from 'react'

/* Redux */
import { shallowEqual } from 'react-redux'

/* Material */
import Typo from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import useStyles from './styles'

/* Types */
import { tListingGroupItem, tProductPreview } from 'src/shared/frontendTypes/product'

/* Components */
import PriceDisplay from 'src/components/PriceDisplay'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import DeliverytimeDisplay from './DeliverytimeDisplay'
import AddToFavButton from 'src/components/AddToFavButton/AddToFavButton'

/* Functions */
import productUrl, { productVariantUrl } from 'src/functions/productUrl'
import getImageUrl from 'src/pages/Product/functions/imageUrl'
import { mergeLivePreviewCommerce, useLiveProductCommerce } from 'src/hooks/useLiveProductCommerce'
import { useProductInquiryConfig } from 'src/context/ProductInquiryContext'
import { post } from 'src/functions/fetch'
import config from 'src/config'
import { isQuantityDiscountAvailable } from 'src/shared/discountShop'
import { useDiscountDateActive } from 'src/hooks/useDiscountDateActive'
import {
  getQuantityDiscountBadgeLabel,
  getQuantityDiscountText,
} from 'src/functions/quantityDiscountText'

/* Custom */
import PreviewImage from './PreviewImage'

const LISTING_GROUP_VISIBLE_COUNT = 5
const LISTING_GROUP_FETCH_COUNT = 5

const toRootRelativeUrl = (value: string) => {
  const url = value.trim()
  if (!url || url.startsWith('/') || url.startsWith('#') || /^[a-z][a-z0-9+.-]*:/i.test(url)) return url
  return `/${url}`
}

const getOptionsFromCombinationKey = (key: string, productId: number) => {
  const prefix = `${productId}-`
  if (!key.startsWith(prefix)) return []

  return key
    .slice(prefix.length)
    .split('-')
    .map(option => Number(option))
    .filter(option => Number.isFinite(option))
}

const replaceVariantText = (title: string, currentLabel?: string, nextLabel?: string) => {
  const safeTitle = String(title || '')
  const from = String(currentLabel || '').replace(/\s+/g, ' ').trim()
  const to = String(nextLabel || '').replace(/\s+/g, ' ').trim()

  if (!from || !to || from === to) return safeTitle

  const index = safeTitle.toLowerCase().indexOf(from.toLowerCase())
  if (index < 0) return safeTitle

  return `${safeTitle.slice(0, index)}${to}${safeTitle.slice(index + from.length)}`
}

const cleanDisplayText = (value?: string) => String(value || '').replace(/\s+/g, ' ').trim()

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const stripBrandPrefix = (title: string, brandTitle?: string) => {
  const cleanTitle = cleanDisplayText(title)
  const brand = cleanDisplayText(brandTitle)
  if (!cleanTitle || !brand) return cleanTitle

  return cleanTitle.replace(new RegExp(`^${escapeRegExp(brand)}\\s*[-–—:|]*\\s*`, 'i'), '').trim() || cleanTitle
}

const containsDisplayText = (title: string, needle?: string) => {
  const cleanNeedle = cleanDisplayText(needle)
  if (!cleanNeedle) return false

  return cleanDisplayText(title).toLocaleLowerCase().includes(cleanNeedle.toLocaleLowerCase())
}

const buildDisplayProductName = ({
  name,
  brandTitle,
  currentColor,
  selectedColor,
}: {
  name: string
  brandTitle?: string
  currentColor?: string
  selectedColor?: string
}) => {
  const cleanName = stripBrandPrefix(name, brandTitle)
  const color = cleanDisplayText(selectedColor)
  if (!color) return cleanName

  const replacedName = replaceVariantText(cleanName, currentColor, color)
  if (containsDisplayText(replacedName, color)) return replacedName

  return replacedName
}

const formatBrandTitle = (brandTitle?: string) => cleanDisplayText(brandTitle).toLocaleUpperCase()

export type display =
  | 'title'
  | 'center'
  | 'attributes'
  | 'hook'
  | 'price'
  | 'deliverytime'
  | 'no-card'
  | `bg-card-${string}`

interface Props {
  product: tProductPreview
  display?: display[]
  variant?: 'grid' | 'highlight' | 'compact' | 'editorial' | 'slider' | 'sliderLine'
  showDiscount?: boolean
  showAvailability?: boolean
  hoverEffect?: boolean
  preferMoodImages?: boolean
  imageSizePx?: number
  emphasizeInStock?: boolean
  showWishlist?: boolean
  requireStock?: boolean
  structuredData?: boolean
  nativeHorizontalCarousel?: boolean
  onOpenProduct?: (product: tProductPreview) => void
}

const Product: React.FC<Props> = ({ 
  product,
  display,
  variant = 'grid',
  showDiscount = true,
  showAvailability = true,
  hoverEffect = true,
  preferMoodImages = false,
  imageSizePx,
  emphasizeInStock = false,
  showWishlist = true,
  requireStock,
  structuredData = false,
  nativeHorizontalCarousel = false,
  onOpenProduct,
}) => {

  const classes = useStyles()
  const productInquiry = useProductInquiryConfig()
  const liveProduct = useLiveProductCommerce(product)
  const productForRender = liveProduct
    ? mergeLivePreviewCommerce(product, liveProduct)
    : product
  const {
    id, key: combinationKey, name, metaTitle, path, price, discount, images, options, attributes,
    deliverytime, stock, onlyStock, inquireOnly,
    brandTitel, ean, sku, mpn,
    listingVariantLabel, listingGroupItems, listingGroupCount, couponPromotion,
  } = productForRender
  const safeImages = Array.isArray(images) ? images : []
  const productImagesForPreview = useMemo(() => {
    const moodImages = safeImages.filter(image => image.type === 'mood')
    const regularImages = safeImages.filter(image => image.type !== 'mood')

    if (preferMoodImages && moodImages.length > 0) return [...moodImages, ...regularImages]
    return regularImages.length > 0 ? regularImages : safeImages
  }, [preferMoodImages, safeImages])
  const safeAttributes = Array.isArray(attributes) ? attributes : []

  const url = productVariantUrl(id, path, combinationKey)

  const d = {
    title:        display ? display.includes('title')        : true,
    price:        display ? display.includes('price')        : true,
    deliverytime: display ? display.includes('deliverytime') : true,
    attributes:   display ? display.includes('attributes')   : true,
    hook:         display ? display.includes('hook')         : false,
    center:       display ? display.includes('center')       : false,
    noCard:       display ? display.includes('no-card')      : false,
  }

  const bgCard = display?.find(d => d.startsWith('bg-card-'))?.split('-')[2] || '#ffffff';
  const compactCard = variant === 'compact' || variant === 'slider' || variant === 'sliderLine' || Boolean(imageSizePx)
  const imageSize = imageSizePx && imageSizePx >= 180 ? 'sm' : compactCard ? 'xs' : 'sm'
  const mobileEditorial = config.productPreview?.mobileStyle === 'editorial'
  const listingGroupItemsForRender = useMemo(() => listingGroupItems || [], [listingGroupItems])
  const [extraListingGroupItems, setExtraListingGroupItems] = useState<tListingGroupItem[]>([])
  const [listingGroupTotalOverride, setListingGroupTotalOverride] = useState<number | null>(null)
  const [listingGroupLoading, setListingGroupLoading] = useState(false)
  const loadedListingGroupItems = useMemo(() => {
    const seen = new Set<string>()
    const items: tListingGroupItem[] = []

    for (const item of [...listingGroupItemsForRender, ...extraListingGroupItems]) {
      if (!item.key || seen.has(item.key)) continue
      seen.add(item.key)
      items.push(item)
    }

    return items
  }, [extraListingGroupItems, listingGroupItemsForRender])
  const variantItems = useMemo(() => {
    const currentImage = productImagesForPreview[0]
    const currentColor = productForRender.crossSellMeta?.farbe
    const currentLabel = listingVariantLabel || currentColor || safeAttributes[0]
    const currentSize = safeAttributes.find(attribute => (
      attribute && attribute !== currentLabel && attribute !== currentColor
    ))
    const currentItem: tListingGroupItem = {
      id,
      key: combinationKey,
      name,
      metaTitle,
      label: currentLabel,
      color: currentColor,
      url,
      image: currentImage,
      size: currentSize,
      price,
      deliverytime,
      discount,
      stock,
      onlyStock,
      couponPromotion,
    }
    const items: tListingGroupItem[] = [currentItem]
    const seen = new Set<string>([combinationKey])

    for (const item of loadedListingGroupItems) {
      if (!item.key || seen.has(item.key)) continue

      const itemOptions = getOptionsFromCombinationKey(item.key, item.id)
      const itemUrl = item.id === id
        && item.color
        && Array.isArray(options)
        && options.length > 1
        && itemOptions.length === options.length
        ? productUrl(id, path, [itemOptions[0], ...options.slice(1)])
        : item.url

      seen.add(item.key)
      items.push({
        ...item,
        url: itemUrl || item.url,
      })
    }

    return items
  }, [
    combinationKey,
    couponPromotion,
    deliverytime,
    discount,
    id,
    loadedListingGroupItems,
    name,
    options,
    path,
    price,
    metaTitle,
    listingVariantLabel,
    productForRender.crossSellMeta?.farbe,
    safeAttributes,
    productImagesForPreview,
    stock,
    onlyStock,
    url,
  ])
  const [selectedVariantKey, setSelectedVariantKey] = useState(combinationKey)
  const [visibleVariantStart, setVisibleVariantStart] = useState(0)
  const touchStartX = useRef<number | null>(null)
  const touchDeltaX = useRef(0)
  const suppressNextClick = useRef(false)
  const selectedVariantIndex = Math.max(
    0,
    variantItems.findIndex(item => item.key === selectedVariantKey),
  )
  const selectedVariant = variantItems[selectedVariantIndex] || variantItems[0]
  const selectedUrl = toRootRelativeUrl(selectedVariant?.url || url)
  const selectedImages = selectedVariant?.image ? [selectedVariant.image] : productImagesForPreview
  const selectedLabel = selectedVariant?.label || selectedVariant?.color || selectedVariant?.size
  const selectedDisplayName = buildDisplayProductName({
    name: selectedVariant?.name || name,
    brandTitle: brandTitel,
    currentColor: productForRender.crossSellMeta?.farbe,
    selectedColor: selectedVariant?.color,
  })
  const selectedBrandTitle = formatBrandTitle(brandTitel)
  const selectedTitle = selectedVariant?.metaTitle
    || replaceVariantText(metaTitle, listingVariantLabel || safeAttributes[0], selectedLabel)
  const displayedAttributes = (() => {
    if (!selectedVariant?.key || selectedVariant.key === combinationKey) return safeAttributes

    const attributesFromVariant = [
      selectedVariant.size,
      selectedLabel && selectedLabel !== selectedVariant.size ? selectedLabel : undefined,
    ].filter((attribute): attribute is string => Boolean(attribute))

    return attributesFromVariant.length > 0 ? attributesFromVariant : safeAttributes
  })()
  const selectedProductId = selectedVariant?.id || id
  const selectedCombinationKey = selectedVariant?.key || combinationKey
  const selectedPrice = typeof selectedVariant?.price === 'number'
    ? selectedVariant.price
    : price
  const selectedDiscount = selectedVariant?.key === combinationKey
    ? discount
    : selectedVariant?.discount
  const selectedDeliverytime = typeof selectedVariant?.deliverytime === 'number'
    ? selectedVariant.deliverytime
    : deliverytime
  const selectedStock = typeof selectedVariant?.stock === 'number'
    ? selectedVariant.stock
    : stock
  const selectedOnlyStock: true | undefined = selectedVariant?.key === combinationKey
    ? onlyStock
    : selectedVariant?.onlyStock === true
      ? true
      : undefined
  const listingGroupTotalCount = Math.max(
    listingGroupTotalOverride || 0,
    listingGroupCount || 0,
    variantItems.length,
  )
  const hasMoreListingGroupItems = Boolean(
    productForRender.listingGroupKey
    && loadedListingGroupItems.length < listingGroupTotalCount
  )
  const canSwipeVariants = listingGroupTotalCount > 1 || variantItems.length > 1
  const canSelectPreviousVariant = selectedVariantIndex > 0
  const canSelectNextVariant = selectedVariantIndex < variantItems.length - 1 || hasMoreListingGroupItems
  const stockQty = selectedStock || 0
  const selectedPriceForDisplay = selectedDiscount
    && showDiscount
    ? {
      regularPrice:  selectedPrice,
      discountPrice: selectedDiscount.price,
      discountPriceAfterStock: selectedDiscount.priceAfterStock,
      stockDiscountLimit: selectedDiscount.stockDiscountLimit,
      limitStockDiscountToStock: selectedDiscount.limitStockDiscountToStock,
      quantityDiscount: selectedDiscount.quantityDiscount,
      title:         selectedDiscount.metaTitle,
      type:          selectedDiscount.type,
      countdownVariant: selectedDiscount.countdownVariant,
      dateEnd:       selectedDiscount.dateEnd,
      showPercent:   selectedDiscount.showPercent,
    }
    : selectedPrice
  const selectedDiscountDateEnd = selectedDiscount?.dateEnd
  const selectedQuantityDiscount = selectedDiscount?.quantityDiscount
  const selectedDiscountDateActive = useDiscountDateActive(selectedDiscountDateEnd)
  const selectedCouponPromotion = selectedVariant?.couponPromotion
    || (selectedVariant?.key === combinationKey ? couponPromotion : undefined)
  const showQuantityDiscount = Boolean(
    d.price
    && selectedDiscount
    && showDiscount
    && selectedDiscountDateActive
    && selectedDiscount.showPercent !== false
    && isQuantityDiscountAvailable(selectedQuantityDiscount, {
      onlyStock: selectedOnlyStock,
      stock: selectedStock,
      dateEnd: selectedDiscountDateEnd,
    }),
  )
  const quantityDiscountBadgeLabel = showQuantityDiscount
    ? getQuantityDiscountBadgeLabel(selectedQuantityDiscount)
    : undefined
  const quantityDiscountCardText = showQuantityDiscount
    ? getQuantityDiscountText(selectedQuantityDiscount)
    : undefined
  const showCouponPromotionBadge = Boolean(
    selectedCouponPromotion?.showOnListing !== false
    && selectedCouponPromotion?.badgeText
  )
  const couponPromotionBadgeClass = selectedCouponPromotion?.color === 'red'
    ? classes.couponPromotionBadgeRed
    : selectedCouponPromotion?.color === 'black'
      ? classes.couponPromotionBadgeBlack
      : classes.couponPromotionBadgeGreen

  useEffect(() => {
    setSelectedVariantKey(combinationKey)
    setVisibleVariantStart(0)
    setExtraListingGroupItems(current => current.length ? [] : current)
    setListingGroupTotalOverride(null)
  }, [combinationKey, productForRender.listingGroupKey])

  useEffect(() => {
    setVisibleVariantStart(current => {
      const visibleCount = Math.min(LISTING_GROUP_VISIBLE_COUNT, variantItems.length)
      const maxStart = Math.max(0, variantItems.length - visibleCount)

      if (selectedVariantIndex < current) return Math.min(selectedVariantIndex, maxStart)
      if (selectedVariantIndex >= current + visibleCount) {
        return Math.min(
          Math.max(0, selectedVariantIndex - visibleCount + 1),
          maxStart,
        )
      }

      return Math.min(current, maxStart)
    })
  }, [selectedVariantIndex, variantItems.length])

  const loadMoreListingGroupItems = async () => {
    if (!productForRender.listingGroupKey || listingGroupLoading || !hasMoreListingGroupItems) return []

    setListingGroupLoading(true)

    try {
      const response = await post({
        url: 'products/listing-group',
        body: {
          shop: config.shop.shop,
          lang: config.shop.lang,
          listingGroupKey: productForRender.listingGroupKey,
          offset: loadedListingGroupItems.length,
          limit: LISTING_GROUP_FETCH_COUNT,
        },
      }) as { count?: unknown, items?: unknown } | null

      const rawResponseItems = response?.items
      const responseItems = Array.isArray(rawResponseItems)
        ? rawResponseItems as tListingGroupItem[]
        : []

      if (typeof response?.count === 'number') {
        setListingGroupTotalOverride(response.count)
      }

      if (responseItems.length > 0) {
        setExtraListingGroupItems(current => {
          const seen = new Set([
            ...listingGroupItemsForRender.map(item => item.key),
            ...current.map(item => item.key),
          ])
          const next = responseItems.filter(item => item.key && !seen.has(item.key))

          return next.length > 0 ? [...current, ...next] : current
        })
      }

      return responseItems
    } finally {
      setListingGroupLoading(false)
    }
  }

  const selectVariantByOffset = (offset: number) => {
    if (!canSwipeVariants) return

    const nextIndex = selectedVariantIndex + offset

    if (nextIndex < 0) return

    if (nextIndex >= variantItems.length) {
      if (offset > 0 && hasMoreListingGroupItems) {
        loadMoreListingGroupItems()
          .then(items => {
            const existingKeys = new Set(variantItems.map(item => item.key))
            const nextItem = items.find(item => item.key && !existingKeys.has(item.key))
            if (nextItem) setSelectedVariantKey(nextItem.key)
          })
      }
      return
    }

    setSelectedVariantKey(variantItems[nextIndex].key)
  }

  const handleVariantTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (!canSwipeVariants) return
    touchStartX.current = event.touches[0]?.clientX ?? null
    touchDeltaX.current = 0
  }

  const handleVariantTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return
    touchDeltaX.current = (event.touches[0]?.clientX ?? touchStartX.current) - touchStartX.current
  }

  const handleVariantTouchEnd = () => {
    if (touchStartX.current === null) return

    const delta = touchDeltaX.current
    touchStartX.current = null
    touchDeltaX.current = 0

    if (Math.abs(delta) < 42) return

    suppressNextClick.current = true
    window.setTimeout(() => {
      suppressNextClick.current = false
    }, 450)
    selectVariantByOffset(delta < 0 ? 1 : -1)
  }

  const handleVariantArrowClick = (offset: number) => (event: MouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (offset < 0 && !canSelectPreviousVariant) return
    if (offset > 0 && !canSelectNextVariant) return
    selectVariantByOffset(offset)
  }

  const openProduct = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) return

    if (suppressNextClick.current) {
      event.preventDefault()
      suppressNextClick.current = false
      return
    }

    if (onOpenProduct && selectedCombinationKey === combinationKey) {
      event.preventDefault()
      onOpenProduct(productForRender)
      return
    }
  }
  const visibleVariantCount = Math.min(LISTING_GROUP_VISIBLE_COUNT, variantItems.length)
  const maxVisibleVariantStart = Math.max(0, variantItems.length - visibleVariantCount)
  const safeVisibleVariantStart = Math.min(visibleVariantStart, maxVisibleVariantStart)
  const visibleVariantEnd = Math.min(
    variantItems.length,
    safeVisibleVariantStart + visibleVariantCount,
  )
  const visibleListingGroupItems = variantItems.slice(safeVisibleVariantStart, visibleVariantEnd)
  const listingGroupBeforeCount = safeVisibleVariantStart
  const listingGroupMoreCount = Math.max(0, listingGroupTotalCount - visibleVariantEnd)
  const hasListingGroupItems = visibleListingGroupItems.length > 1 || listingGroupMoreCount > 0 || listingGroupBeforeCount > 0
  const availabilityFlag = (() => {
    if (!mobileEditorial || !showAvailability || !d.deliverytime) return null

    if (inquireOnly) return {
      className: classes.availabilityFlagInfo,
      label: productInquiry.availabilityLabel,
    }

    if (selectedOnlyStock && stockQty <= 0) return {
      className: classes.availabilityFlagWarning,
      label: 'nicht lieferbar',
    }

    if (selectedOnlyStock && stockQty <= config.onlyStockWarningLimit) return {
      className: classes.availabilityFlagWarning,
      label: `nur ${stockQty}x da`,
    }

    if (stockQty) return {
      className: classes.availabilityFlagSuccess,
      label: stockQty <= config.stockFullQty ? `${stockQty}x sofort` : 'sofort lieferbar',
    }

    if (selectedDeliverytime) return {
      className: classes.availabilityFlagInfo,
      label: `${selectedDeliverytime} Werktage`,
    }

    return null
  })()
  const selectListingGroupItem = (item: tListingGroupItem) => (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setSelectedVariantKey(item.key)
  }
  const showPreviousListingGroupItems = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setVisibleVariantStart(current => Math.max(0, current - LISTING_GROUP_VISIBLE_COUNT))
  }
  const showNextListingGroupItems = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    const needsMoreItems = visibleVariantEnd >= variantItems.length && hasMoreListingGroupItems
    const loadedItems = needsMoreItems ? await loadMoreListingGroupItems() : []
    const nextLength = variantItems.length + loadedItems
      .filter(item => item.key && !variantItems.some(variantItem => variantItem.key === item.key))
      .length
    const nextMaxStart = Math.max(0, nextLength - Math.min(LISTING_GROUP_VISIBLE_COUNT, nextLength))

    setVisibleVariantStart(current => Math.min(current + LISTING_GROUP_VISIBLE_COUNT, nextMaxStart))
  }

  // Keep hook order stable when a live stock update hides or restores the card.
  if (requireStock && (stock || 0) <= 0) return null

  const listingGroupControls = hasListingGroupItems ? (
    <div className={classes.listingGroupControls} aria-label='Weitere Varianten'>
      <button
        type='button'
        className={[
          classes.listingGroupMore,
          classes.listingGroupMoreBefore,
          listingGroupBeforeCount <= 0 ? classes.listingGroupMoreHidden : '',
        ].join(' ')}
        aria-hidden={listingGroupBeforeCount <= 0}
        aria-label={`Vorherige ${listingGroupBeforeCount} Varianten anzeigen`}
        disabled={listingGroupBeforeCount <= 0}
        onClick={showPreviousListingGroupItems}
      >
        +{listingGroupBeforeCount}
      </button>
      {visibleListingGroupItems.map(item => {
        const imageUrl = item.image ? getImageUrl(item.image, item.id, 'xs') : undefined
        const label = item.label || item.color || item.size || 'Variante'
        const active = item.key === selectedCombinationKey

        return (
          <button
            key={`${item.id}-${item.key}`}
            type='button'
            className={[
              classes.listingGroupItem,
              active ? classes.listingGroupItemActive : '',
            ].join(' ')}
            title={label}
            aria-label={label}
            onClick={selectListingGroupItem(item)}
          >
            {imageUrl
              ? <span style={{ backgroundImage: `url(${imageUrl})` }} />
              : <span>{label.slice(0, 1).toUpperCase()}</span>
            }
            <AiGeneratedBadge aiGenerated={item.image?.aiGenerated} size='micro' />
          </button>
        )
      })}
      <button
        type='button'
        className={[
          classes.listingGroupMore,
          listingGroupMoreCount <= 0 ? classes.listingGroupMoreHidden : '',
        ].join(' ')}
        aria-hidden={listingGroupMoreCount <= 0}
        aria-label={`Weitere ${listingGroupMoreCount} Varianten anzeigen`}
        disabled={listingGroupMoreCount <= 0}
        onClick={showNextListingGroupItems}
      >
        +{listingGroupMoreCount}
      </button>
    </div>
  ) : null
  const previewImageMaxSizePercent = mobileEditorial
    ? 96
    : compactCard
      ? canSwipeVariants ? 82 : 90
      : canSwipeVariants ? 84 : 88

  const productCard = <Card 
    className={[
      classes.card,
      emphasizeInStock && typeof stock === 'number' && stock > 0 ? classes.cardInStock : null,
      hoverEffect ? 'hover' : null,
      d.noCard ? classes.noShaddow : null,
    ].join(' ')}
    style={{ backgroundColor: bgCard }}
  >
    <Link href={selectedUrl} legacyBehavior>
      <a onClick={openProduct} className={classes.href}>
        <div
          className={[
            classes.variantImageFrame,
            canSwipeVariants ? classes.variantImageFrameSwipeable : '',
            nativeHorizontalCarousel ? classes.variantImageFrameNativeCarousel : '',
          ].join(' ')}
          // Native scroll-snap owns horizontal gestures in a product row.
          // Variant arrows/thumbnails and the product link remain available.
          onTouchStart={nativeHorizontalCarousel ? undefined : handleVariantTouchStart}
          onTouchMove={nativeHorizontalCarousel ? undefined : handleVariantTouchMove}
          onTouchEnd={nativeHorizontalCarousel ? undefined : handleVariantTouchEnd}
        >
          <PreviewImage
            images={selectedImages}
            productID={selectedProductId}
            metaTitle={selectedTitle}
            heightPx={imageSizePx}
            size={imageSize}
            aspectRatio={compactCard ? undefined : '16 / 9'}
            imageMaxSizePercent={previewImageMaxSizePercent}
          />
          {availabilityFlag && (
            <span className={[
              classes.availabilityFlag,
              availabilityFlag.className,
              showCouponPromotionBadge ? classes.availabilityFlagBelowCoupon : '',
            ].join(' ')}>
              {availabilityFlag.label}
            </span>
          )}
          {showQuantityDiscount && quantityDiscountBadgeLabel && (
            <span className={[
              classes.quantityDiscountFlag,
              showCouponPromotionBadge && availabilityFlag
                ? classes.quantityDiscountFlagBelowCouponAndAvailability
                : showCouponPromotionBadge
                  ? classes.quantityDiscountFlagBelowCoupon
                  : availabilityFlag
                    ? classes.quantityDiscountFlagBelowAvailability
                    : '',
            ].join(' ')}>
              {quantityDiscountBadgeLabel}
            </span>
          )}
          {showCouponPromotionBadge && (
            <span className={[
              classes.couponPromotionBadge,
              couponPromotionBadgeClass,
            ].join(' ')}>
              {selectedCouponPromotion?.badgeText}
            </span>
          )}
          {canSwipeVariants && (
            <>
              <span
                className={[
                  classes.variantSwipeHint,
                  classes.variantSwipeHintLeft,
                  !canSelectPreviousVariant ? classes.variantSwipeHintDisabled : '',
                ].join(' ')}
                onClick={handleVariantArrowClick(-1)}
                aria-hidden='true'
              >
                <KeyboardArrowLeftRoundedIcon fontSize='small' />
              </span>
              <span
                className={[
                  classes.variantSwipeHint,
                  classes.variantSwipeHintRight,
                  !canSelectNextVariant ? classes.variantSwipeHintDisabled : '',
                ].join(' ')}
                onClick={handleVariantArrowClick(1)}
                aria-hidden='true'
              >
                <KeyboardArrowRightRoundedIcon fontSize='small' />
              </span>
            </>
          )}
        </div>

        {( d.title || d.price || d.deliverytime ) &&
          <CardContent
            className={[
              classes.cardContent,
              compactCard ? classes.cardContentCompact : '',
            ].join(' ')}
          >

            {d.title &&
              <div className='card-product-title'>
                {selectedBrandTitle &&
                  <Typo className={classes.productBrand} align={d.center ? 'center' : 'left'}>
                    {selectedBrandTitle}
                  </Typo>
                }
                <Typo className={classes.productName} align={d.center ? 'center' : 'left'}>
                  {selectedDisplayName}
                </Typo>

                {d.attributes &&
                  <ul>
                    {displayedAttributes.map((attribute,i)=>(
                      <li key={i} >
                        {attribute}
                      </li>
                    ))}
                  </ul>
                }

                {d.hook && productForRender.hook &&
                  <Typo className='card-product-hook' align={d.center ? 'center' : 'left'}>
                    {productForRender.hook}
                  </Typo>
                }

              </div>
            }

            {( d.price || d.deliverytime ) &&
              <div className={classes.cardCommerce}>
                {d.price &&
                  <PriceDisplay
                    price={selectedPriceForDisplay}
                    component='p' variant='body1' align='center'
                    className={classes.price}
                    discountBadgeVariant={mobileEditorial ? 'flag' : 'bubble'}
                    onlyStock={selectedOnlyStock}
                    stock={selectedStock}
                  />
                }

                {showQuantityDiscount && quantityDiscountCardText && (
                  <div className={classes.quantityDiscountCardInfo}>
                    <span className={classes.quantityDiscountCardEyebrow}>Aktion</span>
                    <span className={classes.quantityDiscountCardTitle}>
                      {quantityDiscountCardText.title}
                    </span>
                  </div>
                )}

                {d.deliverytime && showAvailability &&
                  <DeliverytimeDisplay
                    {...{
                      deliverytime: selectedDeliverytime,
                      stock: selectedStock,
                      onlyStock: selectedOnlyStock,
                      inquireOnly,
                    }}
                  />
                }
              </div>
            }

          </CardContent>
        }
      </a>
    </Link>

    {listingGroupControls}
    
  </Card>

  return <section className={[
    classes.cardColumn,
    mobileEditorial ? classes.mobileEditorialProduct : '',
  ].join(' ')}>
    {structuredData && <SeoProductCombination {...{
      id, path, name,
      description: metaTitle,
      brandTitel,

      images: safeImages
        .map(image => getImageUrl(image, id, 'xxl'))
        .filter((image): image is string => Boolean(image)),
      
      attributes: safeAttributes.join(', '),
      options,
      price: discount ? discount.price : price,
      regularPrice: discount ? price : undefined,
      priceValidUntil: discount?.dateEnd,
      deliverytime,
      shippingType: productForRender.shippingType,
      combinationKey,
      ean, sku, mpn,

      onlyStock,
      inquireOnly,
      stock,
    }} />}

    {showWishlist &&
      <AddToFavButton
        className={classes.wishlistButton}
        pId={id}
        pKey={selectedCombinationKey}
      />
    }

    {productCard}
  </section>
}

export default memo(Product, (previous, next) => {
  const { display: previousDisplay, ...previousProps } = previous
  const { display: nextDisplay, ...nextProps } = next
  // Builder callers recreate display arrays; compare their small value list.
  // Product objects, callbacks and all other props still use identity equality.
  return shallowEqual(previousProps, nextProps) && (
    previousDisplay === nextDisplay
    || Boolean(previousDisplay && nextDisplay
      && previousDisplay.length === nextDisplay.length
      && previousDisplay.every((value, index) => value === nextDisplay[index]))
  )
})
